import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { useGetStoreStocksQuery, useDeleteStoreStockMutation } from './../slices/storeStocksApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffStoreStock = () => {
  // State variables for filtering, pagination, and date range
  const [filter, setFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch paginated storeStocks data
  const { data, isLoading, error, refetch } = useGetStoreStocksQuery(pageNumber);

  // Mutation for deleting storeStocks
  const [deleteStoreStock, { isLoading: loadingDelete }] = useDeleteStoreStockMutation();

  // Handle storeStock deletion
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeStock?')) {
      try {
        await deleteStoreStock(id).unwrap();
        toast.success('StoreStock deleted successfully!');
        refetch(); // Refetch storeStocks after deletion
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete storeStock.');
      }
    }
  };

  // Filter storeStocks based on date range and search criteria
  const filterStoreStocks = () => {
    if (!data?.storeStocks) return [];

    // 1) Filter by date range
    let filteredStoreStocks = data.storeStocks.filter((storeStock) => {
      const storeStockDate = new Date(storeStock.date);
      return (
        (!startDate || storeStockDate >= new Date(startDate)) &&
        (!endDate || storeStockDate <= new Date(endDate))
      );
    });

    // 2) Apply general search filter against storeStock note and product names within storeStockItems.
    if (filter.trim()) {
      const lowerFilter = filter.toLowerCase();
      filteredStoreStocks = filteredStoreStocks.filter((storeStock) => {
        const noteMatch = storeStock.note?.toLowerCase().includes(lowerFilter);
        const itemMatch = storeStock.storeStockItems.some((item) =>
          item.name.toLowerCase().includes(lowerFilter)
        );
        return noteMatch || itemMatch;
      });
    }

    // 3) Further filter storeStock items by product name ONLY using productSearch.
    if (productSearch.trim()) {
      const lowerProduct = productSearch.toLowerCase();
      filteredStoreStocks = filteredStoreStocks
        .map((storeStock) => {
          return {
            ...storeStock,
            storeStockItems: storeStock.storeStockItems.filter((item) =>
              item.name.toLowerCase().includes(lowerProduct)
            ),
          };
        })
        .filter((storeStock) => storeStock.storeStockItems.length > 0);
    }

    return filteredStoreStocks;
  };

  // Calculate total storeStocks value based on filtered storeStocks
  const calculateTotalStoreStocks = () => {
    return filterStoreStocks().reduce((total, storeStock) => {
      const storeStockTotal = storeStock.storeStockItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );
      return total + storeStockTotal;
    }, 0);
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>MiniStocks</h1>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total StoreStocks: {calculateTotalStoreStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Additional product-only search box */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterStoreStocks().map((storeStock) =>
                storeStock.storeStockItems.map((item) => (
                  <tr key={`${storeStock._id}-${item.product}`}>
                    <td>{new Date(storeStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{(item.qty * item.price).toFixed(2)}</td>
                    <td>{storeStock.note || 'N/A'}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/storeStock/${storeStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        as={Link}
                        to={`/storeStock/${storeStock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(storeStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffStoreStock;
